import styled from "@emotion/styled";
import React from "react";
import { HeaderLogo, HeaderMenuProps, InAppMessagingButton, MenuStyle } from "../header";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";
import UserMenu from "./../header/UserMenu/UserMenu";
import SidebarMenu from "./../header/SidebarMenu/SidebarMenu";
import HorizontalMenu from "./../header/HorizontalMenu/HorizontalMenu";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import { BadgesMenu } from "@kaltura/mediaspace-shared-user-badges";
import { HeaderMoeContainer } from "./HeaderMoeContainer";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import { MoeLocaleChangeConfirmation, MoeLocaleMenu } from "./MoeLocaleMenu";

export interface HeaderMoeProps extends HeaderMenuProps {
    /**
     * url for saving the language the user chose from the locale menu.
     * the locale menu is shown only when it is set (and there are locales to choose from).
     */
    setLocaleUrl?: string;
    /**
     * "are you sure" prompt to show before changing the locale.
     */
    localeChangeConfirmation?: MoeLocaleChangeConfirmation;
}

const StyledHeaderLogo = styled(HeaderLogo)(({ theme }) => ({
    // The following flex props are required to support text-only logo truncation:
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: "auto",
    // we support limited text length, this is the large logo size which is larger
    minWidth: "auto",
    maxWidth: 343,

    margin: theme.spacing(0, 4, 0, 1),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        margin: theme.spacing(0, 1), // we no longer have horizontal items
    },
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        maxWidth: 75,
    },
}));

const StyledHorizontalMenu = styled.div({
    // Not be collapsed in an ugly way when the header is flooded with too many items:
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: "auto",
});

const StyledMenuWrapper = styled.div(({ theme }) => ({
    // Take all available space, to make the header full-width and separate pull-left items from pull-right items:
    flexGrow: 0,

    // Not be collapsed in an ugly way when the header is flooded with too many items:
    flexShrink: 0,
    flexBasis: "auto",

    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    lineHeight: 1,

    // just margin
    marginLeft: theme.spacing(3),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginLeft: 0, // - only on small screen
    },
}));

const StyledUserMenu = styled(UserMenu)({
    margin: 0,
});

const SkipToContentLink = styled.a({
    flexGrow: 0,
    position: "absolute",
    top: -1000,
    left: -1000,
    height: 1,
    width: 1,
    textAlign: "left",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    "&:hover, &:active, &:focus": {
        left: 0,
        top: 0,
        width: "auto",
        height: "auto",
        overflow: "visible",
    },
});

const MenuTrigger = styled.div({
    flexGrow: 0,
});

const Spacer = styled("span")(({ theme }) => ({
    width: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        width: theme.spacing(1),
    },
}));

// HeaderMoe's nav is expected to hold a small, bounded number of top-level items.
// Any items beyond this are folded into a "Show more" dropdown rather than dropped.
const maxNavItems = 4;

/**
 * Site Header Menu component (with navigation, user menu, locale menu..)
 */
export function HeaderMoe({
    title,
    sticky,
    headerColor,
    logo,
    showLogo = true,
    userMenuLabel,
    userMenuItems,
    userName,
    userThumbnailUrl,
    locales,
    currentLocale,
    showNav = true,
    scrollThreshold = 60,
    disableScrollLock = true,
    pages = [],
    topLinks,
    topLinksColor = "translucent",
    menuStyle = MenuStyle.Vertical,
    badgesMenuItems,
    headerActionButtons,
    setLocaleUrl,
    localeChangeConfirmation,
}: HeaderMoeProps) {
    const theme = useTheme();
    const mdScreen = useMediaQuery(theme.breakpoints.down("md"));
    const lgScreen = useMediaQuery(theme.breakpoints.down("lg"));
    const xlScreen = useMediaQuery(theme.breakpoints.up("xl"));

    // calculate top links, taking into account the menu max length
    const maxTopLinks = Math.min(topLinks ? topLinks.length : 0, xlScreen ? 6 : lgScreen ? 3 : 4);

    /*
     * The following factors are taken into consideration while deciding to render horizontal or vertical menu:
     * 1. Menu style from the props - the parent component could request to render vertical menu no matter what.
     * 2. Screen size - always render vertical menu on small screens.
     *
     * Unlike the plain HeaderMenu, HeaderMoe's nav is a small, bounded set of items (up to maxNavItems),
     * so instead of dynamically measuring available width (fragile, and prone to rendering the nav
     * in an unstyled/uncollapsed state), it's rendered with the plain HorizontalMenu: up to maxNavItems
     * items directly in the header, with any extra items folded into a "Show more" dropdown.
     */
    const showVerticalMenu = menuStyle === MenuStyle.Vertical || mdScreen;

    // Determine if the burger menu should be displayed
    const shouldDisplayMenu = showNav && showVerticalMenu && (pages?.length ?? 0) > 0;

    const config = useConfig();

    const showLocaleMenu = !!setLocaleUrl && !!locales?.length && !!currentLocale;

    return (
        <ButtonAnalyticsProvider location={"header"}>
            <HeaderMoeContainer sticky={sticky} scrollThreshold={scrollThreshold} headerColor={headerColor}>
                <nav>
                    <SkipToContentLink href={"#contentWrap"} className={"skip-to-content-link"}>
                        {translate("Skip to main content")}
                    </SkipToContentLink>
                </nav>
                {/* Vertical menu*/}
                {shouldDisplayMenu && (
                    <MenuTrigger className={`kms-ds-menu-trigger`}>
                        <SidebarMenu pages={pages} topLinks={topLinks} label={title} buttonColor={"secondary"} />
                    </MenuTrigger>
                )}
                {/* HeaderLogo is shown when showLogo is true and either on large screens or when search is closed on small screens */}
                {showLogo && <StyledHeaderLogo {...logo} className={"kms-ds-header-menu-logo"} />}

                {/* Horizontal menu (always show the container because it has the "flex-grow" style) */}
                <StyledHorizontalMenu className={"kms-ds-header-menu-horizontal"}>
                    {showNav && !showVerticalMenu && (
                        <nav aria-label={title}>
                            <HorizontalMenu
                                pages={pages}
                                maxItems={maxNavItems}
                                showMore={true}
                                openOnHover={true}
                                isTab={true}
                                noMaxWidth={true}
                                label={title}
                                buttonColor={"secondary"}
                                buttonVariant={"borderless"}
                                buttonSize={"large"}
                                disableScrollLock={disableScrollLock}
                            />
                        </nav>
                    )}
                </StyledHorizontalMenu>

                <StyledMenuWrapper className={"kms-ds-header-menu-search-wrap"}>
                    {/* top links */}
                    {!!topLinks?.length && !mdScreen && (
                        <HorizontalMenu
                            pages={topLinks}
                            maxItems={maxTopLinks}
                            buttonVariant={"pill"}
                            buttonColor={topLinksColor}
                            noMaxWidth={true}
                            label={translate("Additional items")}
                            disableScrollLock={disableScrollLock}
                        />
                    )}

                    {config.inAppMessaging && <InAppMessagingButton />}
                </StyledMenuWrapper>

                {badgesMenuItems
                    && (badgesMenuItems.earnedBadges.length > 0 || badgesMenuItems.badgesToEarn.length > 0) && (
                        <>
                            <Spacer />
                            <BadgesMenu badges={badgesMenuItems} />
                        </>
                    )}

                {(userMenuItems || showLocaleMenu) && <Spacer />}

                {userMenuItems && (
                    <StyledUserMenu
                        className={"kms-ds-header-menu-user-menu"}
                        disableScrollLock={disableScrollLock}
                        items={userMenuItems}
                        label={userMenuLabel}
                        userName={userName}
                        userThumbnailUrl={userThumbnailUrl}
                        buttonColor={"secondary"}
                        buttonSize={"large"}
                    />
                )}
                {showLocaleMenu && (
                    <MoeLocaleMenu
                        locales={locales}
                        currentLocale={currentLocale}
                        setLocaleUrl={setLocaleUrl}
                        localeChangeConfirmation={localeChangeConfirmation}
                        disableScrollLock={disableScrollLock}
                    />
                )}
                {headerActionButtons && <Spacer />}
                {headerActionButtons?.map((button, index) => <CustomComponent key={index} component={button} />)}
            </HeaderMoeContainer>
        </ButtonAnalyticsProvider>
    );
}

export default HeaderMoe;
